import {
  isBefore,
  parse,
  endOfMonth,
  isValid,
  startOfDay,
  isAfter,
} from 'date-fns';
import * as yup from 'yup';

export interface AmountFormValues {
  amount: string;
}

export interface AccountTypeFormValues {
  name: string;
  accountNumber: string | '';
  routingNumber: string | '';
  nickname: string | '';
}

export interface CardTypeFormValues {
  name: string;
  cardNumber: string;
  expirationDate: string;
  zipCode: string;
  nickname: string;
}

export const checkDisabled = (error = '') => {
  if (error === validationMessages.required) {
    return true;
  }
  return false;
};

export const validationMessages = {
  required: 'Amount must be at least $0.01',
  moreThanMin: 'You are paying more than the minimum due.',
  moreThanPayInFull: 'You are paying more than the Pay in full amount.',
  lessThanMin: 'You are paying less than the minimum due.',
};

export const paymentAmountValidationSchema = (
  totalDue: string,
  minimumDue: string,
  dueDate: string
) =>
  yup.object({
    amount: yup
      .number()
      .transform(function (value, originalValue) {
        // This transforms handles empty string values not throwing a yup type error.
        // Basically, if an empty string is passed to this schema it will convert it to null, otherwise it will return the original value

        if (this.isType(value)) return value;
        if (!originalValue || !originalValue.trim()) {
          return null;
        }
        // we return the invalid original value
        return originalValue;
      })
      .nullable()
      .transform(function (value, originalValue) {
        // This transforms handles empty string values not throwing a yup type error.
        // Basically, if an empty string is passed to this schema it will convert it to null, otherwise it will return the original value
        if (this.isType(value)) return value;
        if (!originalValue || !originalValue.trim()) {
          return null;
        }
        // we return the invalid original value
        return originalValue;
      })
      .nullable()
      .required(validationMessages.required)
      .moreThan(0.0, validationMessages.required)
      .test(
        'is-more-than-pay-in-full',
        validationMessages.moreThanPayInFull,
        (val) => (val as number) <= Number(totalDue)
      )
      .test('is-also-less-than', validationMessages.lessThanMin, (val) => {
        const parsedDueDate = parse(dueDate, 'MM/dd/yyyy', new Date());
        const isPastDue = isBefore(parsedDueDate, new Date());
        return isPastDue || (val as number) >= Number(minimumDue);
      })
      .test('is-also-more-than', validationMessages.moreThanMin, (val) => {
        return (
          (val as number) <= Number(minimumDue) ||
          (val as number) > Number(totalDue)
        );
      }),
  });

export const cardPaymentTypeValidationMessages: {
  [fieldName: string]: string;
} = {
  name: 'Name must be at least 3 letters with no special characters',
  cardNumber: 'Enter a valid credit card number',
  expirationDate: 'Enter a valid expiration date',
  zipCode: 'Enter a valid 5-digit zip code',
  nickname: 'Nickname must be at least 1 character with no special characters',
};

export const cardPaymentTypeValidationSchema = (ignore = false) =>
  yup.object({
    name: yup
      .string()
      .required(cardPaymentTypeValidationMessages.name)
      .min(3, cardPaymentTypeValidationMessages.name)
      .matches(/^[a-zA-Z '-]+$/, cardPaymentTypeValidationMessages.name),
    expirationDate: yup
      .string()
      .required(cardPaymentTypeValidationMessages.expirationDate)
      .min(5, cardPaymentTypeValidationMessages.expirationDate)
      .test(
        'validDate',
        cardPaymentTypeValidationMessages.expirationDate,
        (val) => {
          if (!val) return false;
          try {
            const parsedDate = parse(val, 'MM/yy', new Date());
            if (!isValid(parsedDate)) return false;
            const endOfExpirationMonth = startOfDay(endOfMonth(parsedDate));
            const now = startOfDay(new Date());
            return !isAfter(now, endOfExpirationMonth);
          } catch {
            return false;
          }
        }
      ),
    zipCode: yup
      .string()
      .required(cardPaymentTypeValidationMessages.zipCode)
      .test('length', cardPaymentTypeValidationMessages.zipCode, (val) =>
        val ? val.toString().length === 5 : false
      ),
    nickname: yup
      .string()
      .test('nickname', cardPaymentTypeValidationMessages.nickname, (val) => {
        if (val && val?.toString().length > 0) {
          if (val?.match(/^[a-zA-Z0-9 '-]+$/)) {
            return true;
          }
          return false;
        }

        return true;
      }),
    ...(!ignore
      ? {
          cardNumber: yup
            .string()
            .required(cardPaymentTypeValidationMessages.cardNumber)
            .test(
              'length',
              cardPaymentTypeValidationMessages.cardNumber,
              (val) =>
                val
                  ? val.toString().length >= 15 &&
                    (/^[0-9]*$/.test(val.toString()) ||
                      val.toString().includes('•'))
                  : false
            ),
        }
      : {}),
  });

export const accountPaymentTypeValidationMessages: { [field: string]: string } =
  {
    name: 'Name must be at least 3 letters with no special characters',
    routingNumber: 'Enter a valid 9-digit routing number',
    accountNumber: 'Enter a valid account number',
    nickname:
      'Nickname must be at least 1 character with no special characters',
  };

export const accountPaymentTypeValidationSchema = (ignore = false) =>
  yup.object({
    ...(!ignore
      ? {
          routingNumber: yup
            .string()
            .required(accountPaymentTypeValidationMessages.routingNumber)
            .test(
              'length',
              accountPaymentTypeValidationMessages.routingNumber,
              (val) =>
                val
                  ? val.toString().length === 9 &&
                    (/^[0-9]*$/.test(val.toString()) ||
                      val.toString().includes(String.fromCharCode(8226)))
                  : false
            ),
          accountNumber: yup
            .string()
            .required(accountPaymentTypeValidationMessages.accountNumber)
            .test(
              'length',
              accountPaymentTypeValidationMessages.accountNumber,
              (val) =>
                val
                  ? val.toString().length >= 4 &&
                    (/^[0-9]*$/.test(val.toString()) ||
                      val.toString().includes(String.fromCharCode(8226)))
                  : false
            ),
        }
      : {}),
    name: yup
      .string()
      .required(accountPaymentTypeValidationMessages.name)
      .min(3, accountPaymentTypeValidationMessages.name)
      .matches(/^[a-zA-Z '-]+$/, accountPaymentTypeValidationMessages.name),
    nickname: yup
      .string()
      .test('nickname', cardPaymentTypeValidationMessages.nickname, (val) => {
        if (val && val?.toString().length > 0) {
          if (val?.match(/^[a-zA-Z0-9 '-]+$/)) {
            return true;
          }
          return false;
        }

        return true;
      }),
  });
